<template>
  <div class="w-full h-full pt-10 px-3">
    <loader v-if="isLoading" size="xxs" :loader-image="false" />
    <template v-else>
      <div class="flex flex-row justify-start items-center gap-4">
        <h1 class="text-xl font-extrabold">Employees</h1>
        <Breadcrumb :items="breadcrumbs" />
      </div>
      <div class="mt-6">
        <Tabs :tabs="tabsItems" :active-tab="activeTab" @currentTab="onTabChange($event)" :border="true" />
      </div>
      <div class="flex flex-col gap-2">
        <Card class="mt-6 p-5 flex flex-col gap-5">
          <template v-slot:footer>
            <CardFooter
              reloadcard
              :show-filter="false"
              @reload="onGetQuery()"
              @sortType="sortType = $event"
              @searchResult="searchTerm = $event"
            />
          </template>
        </Card>
        <sTable
          v-if="filteredEmployees.length || isFetching"
          :headers="headers"
          :items="filteredEmployees"
          :loading="isFetching"
          :pagination-list="pagination"
          @page="onGetQuery({ page: $event })"
          @itemsPerPage="onGetQuery({ perPage: $event })"
          page-sync
        >
          <template v-slot:item="{ item }">
            <div class="flex items-center whitespace-nowrap" v-if="item.userId">
              <img
                v-if="item.data.photo"
                :src="item.data.photo"
                alt="profile pic"
                class="w-8 h-8 rounded object-cover"
              />
              <div
                v-else
                class="w-8 h-8 rounded text-blueCrayola border text-xs font-semibold flex items-center justify-center"
              >
                {{ $getInitials(`${item.data.fname} ${item.data.lname}`) }}
              </div>
              <div class="ml-2 text-sm flex flex-col justify-start">
                <span class="font-bold" style="color: #525252">
                  {{ item.data.fname }} {{ item.data.lname }}
                </span>
                <span class="text-romanSilver text-xs uppercase">
                  {{ item.data.designation || "-" }}
                </span>
              </div>
            </div>
            <div v-else-if="item.function" class="text-sm text-jet">
              {{ item.data.function || "-" }}
            </div>
            <div v-else-if="item.level" class="text-sm text-jet">
              {{ item.data.level || "-" }}
            </div>
            <div v-else-if="item.employmentType" class="text-sm text-jet">
              {{ item.data.employmentType || "-" }}
            </div>
            <span v-else-if="item.engagementDate" class="text-sm text-jet">
              {{
                item.data.engagementDate
                  ? $DATEFORMAT(new Date(item.data.engagementDate), "MMMM dd, yyyy")
                  : "-"
              }}
            </span>
            <div v-else-if="item.contractEndDate" class="flex flex-col text-sm text-jet">
              <span>
                {{
                  item.data.contractEndDate
                    ? $DATEFORMAT(new Date(item.data.contractEndDate), "MMMM dd, yyyy")
                    : "-"
                }}
              </span>
              <span
                class="text-xs text-romanSilver"
                v-if="item.data.daysLeft !== undefined && item.data.daysLeft !== null"
              >
                {{ item.data.daysLeft >= 0 ? `${item.data.daysLeft}d left` : `${Math.abs(item.data.daysLeft)}d overdue` }}
              </span>
            </div>
            <p
              v-else-if="item.status"
              class="text-center capitalize mx-auto status-badge"
              :class="statusClass(item.data.status)"
            >
              {{ formatStatus(item.data.status) }}
            </p>
            <span v-else-if="item.id">
              <Menu left top="40" margin="24" class="my-2 p-0">
                <template v-slot:title>
                  <icon icon-name="more_icon" size="xs" />
                </template>
                <div class="w-48 flex flex-col justify-start items-center gap-3 px-3 py-2">
                  <div
                    class="w-full"
                    v-if="canExtend(item.data.status)"
                    @click="onExtendContract(item.data)"
                  >
                    <p class="font-normal text-sm text-darkPurple cursor-pointer">Renew Contract</p>
                  </div>
                  <div class="w-full" v-else @click="$refs.view_details.toggle(item.data)">
                    <p class="font-normal text-sm text-darkPurple cursor-pointer">View Details</p>
                  </div>
                  <div class="w-full" @click="$refs.renewal_history.toggle(item.data)">
                    <p class="font-normal text-sm text-darkPurple cursor-pointer">Renewal History</p>
                  </div>
                </div>
              </Menu>
            </span>
          </template>
        </sTable>
        <div v-else class="w-full flex flex-col justify-center items-center">
          <icon icon-name="empty_agreement" size="l" style="width: 300px" />
          <div class="w-1/2 text-base text-center">
            There are no {{ activeTab === "Active Employees" ? "active" : "inactive" }} contract employees to display.
          </div>
        </div>
      </div>
      <RenewalHistory ref="renewal_history" @viewDetails="$refs.view_details.toggle($event)" />
      <ViewDetails ref="view_details" @trail="$refs.approval_status.toggle($event)" />
      <ApprovalProgress ref="approval_status" />
    </template>
  </div>
</template>

<script>
import Breadcrumb from "@scelloo/cloudenly-ui/src/components/bread-crumb";
import STable from "@scelloo/cloudenly-ui/src/components/table";
import Card from "@scelloo/cloudenly-ui/src/components/card";
import Tabs from "@scelloo/cloudenly-ui/src/components/tab";
import Menu from "@scelloo/cloudenly-ui/src/components/Menu";
import CardFooter from "@/components/CardFooter";

export default {
  name: "ContractEmployees",
  components: {
    Breadcrumb,
    STable,
    Card,
    Tabs,
    CardFooter,
    Menu,
    ViewDetails: () =>
      import("@/modules/Admin/EmployeeModule/EmployeeActions/ContractEmployees/viewDetails"),
    RenewalHistory: () =>
      import("@/modules/Admin/EmployeeModule/EmployeeActions/ContractEmployees/renewalHistory"),
    ApprovalProgress: () => import("@/components/ApprovalStatus"),
  },
  data() {
    return {
      isLoading: true,
      isFetching: true,
      activeTab: "Active Employees",
      tabsItems: ["Active Employees", "Inactive Employees"],
      searchTerm: "",
      sortType: "",
      queryParams: {},
      employees: [],
      pagination: {},
      headers: [
        { title: "Employee Name", value: "userId" },
        { title: "Org Unit", value: "function" },
        { title: "Job Level", value: "level" },
        { title: "Employment Type", value: "employmentType" },
        { title: "Engagement Date", value: "engagementDate" },
        { title: "Contract End", value: "contractEndDate" },
        { title: "Renewal Status", value: "status" },
        { title: "", value: "id", image: true },
      ],
      breadcrumbs: [
        { disabled: false, text: "Employee", href: "", id: "Employee" },
        {
          disabled: false,
          text: "Contract Employees",
          href: "ContractEmployees",
          id: "ContractEmployees",
        },
      ],
    };
  },

  computed: {
    filteredEmployees() {
      let list = [...this.employees];

      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        list = list.filter((employee) =>
          `${employee.fname} ${employee.lname}`.toLowerCase().includes(term)
        );
      }

      if (this.sortType === "fname:asc") {
        list.sort((a, b) => a.fname.localeCompare(b.fname));
      } else if (this.sortType === "fname:desc") {
        list.sort((a, b) => b.fname.localeCompare(a.fname));
      } else if (this.sortType === "createdAt:desc") {
        list.sort((a, b) => new Date(b.engagementDate) - new Date(a.engagementDate));
      }

      return list;
    },
  },

  methods: {
    onTabChange(tab) {
      this.activeTab = tab;
      this.onGetQuery({ page: 1 });
    },
    onGetQuery(queryParams) {
      if (queryParams !== undefined) {
        this.queryParams = { ...this.queryParams, ...queryParams };
      } else {
        this.queryParams = {};
      }
      this.getContractEmployees();
    },
    canExtend(status) {
      return !["pending", "approved"].includes(status);
    },
    formatStatus(status) {
      return status.split("_").join(" ");
    },
    statusClass(status) {
      return {
        approved: status === "approved",
        pending: status === "pending",
        "not-started": status === "not_started",
        disapproved: status === "disapproved" || status === "cancelled",
      };
    },
    onExtendContract(employee) {
      this.$router.push({ name: "NewContractRenewal", params: { employeeInfo: employee } });
    },
    getDaysLeft(contractEndDate) {
      if (!contractEndDate) return null;
      return Math.ceil((new Date(contractEndDate) - new Date()) / (1000 * 60 * 60 * 24));
    },
    async getContractEmployees() {
      this.isFetching = true;
      try {
        const status = this.activeTab === "Active Employees" ? "active" : "inactive";
        const { data } = await this.$_getContractEmployees({ status, ...this.queryParams });
        this.employees = (data.data || []).map((employee) => ({
          ...employee,
          id: employee.userId,
          status: employee.status || "not_started",
          daysLeft: this.getDaysLeft(employee.contractEndDate),
        }));
        this.pagination = data.meta || {};
      } catch (error) {
        this.employees = [];
        this.$toasted.error(error?.message || error, { duration: 5000 });
      } finally {
        this.isFetching = false;
        this.isLoading = false;
      }
    },
  },

  created() {
    this.getContractEmployees();
  },
};
</script>

<style scoped>
.status-badge {
  width: max-content;
  max-width: max-content;
  padding: 8px;
  border-radius: 4px;
}
.pending {
  background: rgba(233, 147, 35, 0.08);
  color: #e99323;
}
.approved {
  background: rgba(19, 181, 106, 0.08);
  color: #13b56a;
}
.disapproved {
  background: rgba(234, 60, 83, 0.08);
  color: #ea3c53;
}
.not-started {
  background: rgba(234, 60, 83, 0.08);
  color: #ea3c53;
}
</style>
